import { motion } from 'framer-motion'
import { useState } from 'react'

interface MotionImageProps {
  src: string
  alt: string
  delay?: number
  className?: string
  direction?: 'up' | 'down' | 'left' | 'right'
  exit?: boolean
  width?: number | string
  height?: number | string
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'
}

export const MotionImage = ({
  src,
  alt,
  delay = 0,
  className = '',
  direction = 'up',
  exit = true,
  width,
  height,
  objectFit = 'cover'
}: MotionImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false)

  // Determine initial and exit positions based on direction
  const getDirectionalOffsets = () => {
    switch (direction) {
      case 'up':
        return { initial: { y: 20 }, exit: { y: -20 } }
      case 'down':
        return { initial: { y: -20 }, exit: { y: 20 } }
      case 'left':
        return { initial: { x: 20 }, exit: { x: -20 } }
      case 'right':
        return { initial: { x: -20 }, exit: { x: 20 } }
      default:
        return { initial: { y: 20 }, exit: { y: -20 } }
    }
  }

  const { initial, exit: exitOffset } = getDirectionalOffsets()

  const variants = {
    initial: { 
      opacity: 0,
      ...initial
    },
    animate: { 
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
        delay: isLoaded ? delay : 0
      }
    },
    exit: exit ? {
      opacity: 0,
      ...exitOffset,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    } : {}
  }

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
    >
      <motion.img
        src={src}
        alt={alt}
        onLoad={() => setIsLoaded(true)}
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ 
          opacity: isLoaded ? 1 : 0,
          scale: isLoaded ? 1 : 1.1,
          transition: { duration: 0.3 }
        }}
        className={`w-full h-full transition-transform hover:scale-105`}
        style={{ objectFit }}
      />
    </motion.div>
  )
}

export default MotionImage